import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import * as React from 'react';
import { FC } from 'react';
import SubmitButton from '../SubmitButton';
import { FLAT_BTN } from '../../lib/constants';
import { ScopeApprovalPolicy, ScopeDocument } from '../../CommonInterfaces';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import Input from '@mui/material/Input';
import { ReasonCode, ScopeReviewer } from '../../services/data/ReservationScopeInterfaces';
import { ReservationScopeService } from '../../services/ReservationScopeService';
import FormHelperText from '@mui/material/FormHelperText';

const styles = {
  documentList: {
    listStyleType: 'none',
  },
  redWarning: {
    color: 'red',
  },
};

interface ScopeSubmissionModalProps {
  onSubmitButtonClick(
    scopeApprovalPolicy: string,
    scopeManagerApprovalEmail: string,
    scopeReasonCodeId: number,
  ): Promise<void>;
  onCancelButtonClick(): void;
  scopeDocuments: ScopeDocument[];
  depositorCompanyName: string;
  warehouseCompanyName: string;
}

const ScopeSubmissionModal: FC<ScopeSubmissionModalProps> = (props) => {
  const [submitingForApproval, setSubmitingForApproval] = React.useState<boolean>(false);
  const [scopeDocumentChecked, setScopeDocumentChecked] = React.useState<boolean[]>(
    new Array(props.scopeDocuments.length).fill(false),
  );
  const [scopeApprovalPolicy, setScopeApprovalPolicy] = React.useState<string>(ScopeApprovalPolicy.manager_approval);
  const [scopeManagerApprovalEmail, setScopeManagerApprovalEmail] = React.useState<string>('');
  const [scopeReasonCodeId, setScopeReasonCodeId] = React.useState<number | null>(null);
  const [scopeReasonCodes, setScopeReasonCodes] = React.useState<ReasonCode[]>([]);
  const [allowedScopeReviewers, setAllowedScopeReviewers] = React.useState<ScopeReviewer[]>([]);

  React.useEffect(() => {
    (async () => {
      const service = new ReservationScopeService();
      const [reasonCodes, reviewers] = await Promise.all([
        service.retrieveScopeReasonCodes(),
        service.getAllowedScopeReviewers(),
      ]);
      setScopeReasonCodes(reasonCodes);
      setAllowedScopeReviewers(reviewers);
    })();
  }, []);

  const submitButtonClicked = async () => {
    setSubmitingForApproval(true);
    await props.onSubmitButtonClick(scopeApprovalPolicy, scopeManagerApprovalEmail, scopeReasonCodeId);
    setSubmitingForApproval(false);
  };

  const cancelButtonClicked = () => {
    props.onCancelButtonClick();
  };

  return (
    <Dialog open={true}>
      <DialogTitle>Submit Scope for Approval?</DialogTitle>
      <DialogContent>
        {props.scopeDocuments.length !== 0 && renderScopeDocsFinalCheck(props.scopeDocuments)}
        {renderReasonCodeOption()}
        {renderApprovalMethodOption()}
      </DialogContent>
      <DialogActions>
        <Button onClick={cancelButtonClicked} disabled={submitingForApproval}>
          Cancel
        </Button>
        <SubmitButton
          variant="text"
          label="Submit"
          onClick={submitButtonClicked}
          showSpinner={submitingForApproval}
          disabled={
            submitingForApproval ||
            scopeDocumentChecked.some((v) => v === false) ||
            scopeReasonCodeId === null ||
            (scopeManagerApprovalEmail === '' && scopeApprovalPolicy === ScopeApprovalPolicy.manager_approval)
          }
        />
      </DialogActions>
    </Dialog>
  );

  function renderScopeDocsFinalCheck(scopeDocs: ScopeDocument[]) {
    return (
      <div>
        <label>
          <b>Scope Documents Final Review</b>
        </label>
        <ul style={styles.documentList}>
          {scopeDocs.map((doc, index) => {
            return renderSubmitScopeApprovalCheck(doc, index);
          })}
        </ul>
        <p>
          <i>* Warehouse:</i> <b>{props.warehouseCompanyName}</b>
        </p>
        <p>
          <i>* Depositor:</i> <b>{props.depositorCompanyName}</b>
        </p>
        {!scopeDocumentChecked.every((v) => v === true) && (
          <p style={styles.redWarning}>
            <i>* Check all documents before submitting.</i>
          </p>
        )}
        <hr />
      </div>
    );
  }

  function renderReasonCodeOption() {
    return (
      <div>
        <FormControl variant="standard">
          <label htmlFor="scope-reason-code">
            <b>Scope Change Reason Code:</b>
          </label>
          <Select
            variant="standard"
            native
            id="scope-reason-code"
            name="scopeReasonCode"
            value={scopeReasonCodeId === null ? '' : scopeReasonCodeId}
            onChange={(event) => setScopeReasonCodeId(parseInt(event.target.value as string, 10))}
          >
            <option value={null}>Select a reason code *</option>
            {scopeReasonCodes.map((reasonCode) => (
              <option key={reasonCode.id} value={reasonCode.id}>
                {reasonCode.displayName}: {reasonCode.description}
              </option>
            ))}
          </Select>
          <FormHelperText>Required</FormHelperText>
        </FormControl>
      </div>
    );
  }

  function renderApprovalMethodOption() {
    return (
      <div>
        <FormControl variant="standard">
          <label htmlFor="scope-approval-policy">
            <b>Set Scope Approval Policy:</b>
          </label>
          <Select
            variant="standard"
            native
            id="scope-approval-policy"
            name="scopeApprovalPolicy"
            value={scopeApprovalPolicy}
            onChange={(event) => setScopeApprovalPolicy(event.target.value)}
          >
            <option value={ScopeApprovalPolicy.manager_approval}>Manager approval policy</option>
            <option value={ScopeApprovalPolicy.admin_approval}>Admin approval policy - automatic approval</option>
          </Select>
        </FormControl>
        {scopeApprovalPolicy === ScopeApprovalPolicy.manager_approval && (
          <div>
            <FormControl variant="standard">
              <label htmlFor="scope-manager-approval-email">
                <b>Manager Email:</b>
              </label>
              <Select
                variant="standard"
                native
                id="scope-manager-approval-email"
                name="scopeManagerApprovalEmail"
                value={scopeManagerApprovalEmail}
                onChange={(event) => {
                  setScopeManagerApprovalEmail(event.target.value);
                }}
              >
                <option value="">Select a manager *</option>
                {allowedScopeReviewers.map((reviewer) => (
                  <option key={reviewer.email} value={reviewer.email}>
                    {reviewer.name} ({reviewer.email})
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormHelperText>Required</FormHelperText>
            <p>
              This will send an email to manager requesting a review of this scope. Are you sure you want to proceed?
            </p>
          </div>
        )}
        {scopeApprovalPolicy === ScopeApprovalPolicy.admin_approval && (
          <p>This will automatically approve this scope. Are you sure you want to proceed?</p>
        )}
      </div>
    );
  }

  function renderSubmitScopeApprovalCheck(doc: ScopeDocument, index: number) {
    return (
      <li key={index}>
        <FormControlLabel
          control={
            <Checkbox
              className="checkbox"
              name={index.toString()}
              checked={scopeDocumentChecked[index]}
              onChange={(event) => {
                setDocumentChecked(index, event.target.checked);
              }}
            />
          }
          labelPlacement="end"
          label={
            <p>
              Document&nbsp;
              <a href={`//${window.location.host}/v2/file-storage/content/${doc.fileKey}`} target="_blank">
                {doc.fileName}
              </a>
              {doc.visibleToShipper === true && doc.visibleToWarehouse === false && (
                <span>
                  &nbsp; is visible to <b>Depositor</b>, it does not contain any Warehouse information.
                </span>
              )}
              {doc.visibleToShipper === false && doc.visibleToWarehouse === true && (
                <span>
                  &nbsp; is visible to <b>Warehouse</b>, it does not contain any Depositor information.
                </span>
              )}
              {doc.visibleToShipper === false && doc.visibleToWarehouse === false && (
                <span>
                  &nbsp; is visible only to <b>Flexe Admins</b>.
                </span>
              )}
              {doc.visibleToShipper === true && doc.visibleToWarehouse === true && (
                <span>
                  &nbsp; is visible to <b>Depositor</b> and <b>Warehouse</b>.
                </span>
              )}
            </p>
          }
        />
      </li>
    );
  }

  function setDocumentChecked(index: number, checked: boolean) {
    const newScopeDocumentChecked = [...scopeDocumentChecked];
    newScopeDocumentChecked[index] = checked;
    setScopeDocumentChecked(newScopeDocumentChecked);
  }
};

export default ScopeSubmissionModal;
